import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  FormHelperText,
  TextField,
  Typography,
  Chip,
  CircularProgress,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { ColorGrid } from './ColorGrid';
import { EditField, Make, Model, Color, Body, VehicleType, Flag, Dataset } from '../types';

const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: 300,
    },
  },
};

interface EditDialogProps {
  open: boolean;
  onClose: () => void;
  editField: EditField | null;
  editValue: any;
  setEditValue: (value: any) => void;
  editModelValue: string;
  setEditModelValue: (value: string) => void;
  onSave: () => void;
  saving: boolean;
  isIndividual: boolean;
  selectedCount: number;
  // Dropdown data (sorted)
  makes: Make[];
  colors: Color[];
  bodies: Body[];
  types: VehicleType[];
  flags: Flag[];
  datasets: Dataset[];
  // For model selection when editing make
  availableModels: Model[];
}

const getDialogTitle = (field: EditField | null, isIndividual: boolean, selectedCount: number): string => {
  if (!field) return '';

  const fieldLabels: Record<EditField, string> = {
    make: 'Make & Model',
    model: 'Model',
    colors: 'Colors',
    body: 'Body',
    type: 'Type',
    year: 'Year',
    vin: 'VIN',
    flags: 'Flags',
    dataset: 'Dataset',
    colorChecked: 'Color Verified',
    clearBodyTypeYear: 'Clear Body/Type/Year',
  };

  const label = fieldLabels[field] || field.charAt(0).toUpperCase() + field.slice(1);
  return isIndividual ? `Edit ${label}` : `Bulk Edit ${label} (${selectedCount} items)`;
};

export const EditDialog: React.FC<EditDialogProps> = ({
  open,
  onClose,
  editField,
  editValue,
  setEditValue,
  editModelValue,
  setEditModelValue,
  onSave,
  saving,
  isIndividual,
  selectedCount,
  makes,
  colors,
  bodies,
  types,
  flags,
  datasets,
  availableModels,
}) => {
  const isSaveDisabled = saving || (editField === 'make' && editValue && !editModelValue);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{getDialogTitle(editField, isIndividual, selectedCount)}</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1 }}>
          {/* Make Field - includes Model selection */}
          {editField === 'make' && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <FormControl fullWidth>
                <InputLabel id="edit-make-label">Make</InputLabel>
                <Select
                  labelId="edit-make-label"
                  value={editValue || ''}
                  onChange={(e) => {
                    setEditValue(e.target.value);
                    setEditModelValue('');
                  }}
                  input={<OutlinedInput label="Make" />}
                  MenuProps={MenuProps}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {makes.map((make) => (
                    <MenuItem key={make.id} value={make.id}>
                      {make.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth disabled={!editValue} error={!!editValue && !editModelValue}>
                <InputLabel id="edit-make-model-label">Model</InputLabel>
                <Select
                  labelId="edit-make-model-label"
                  value={editModelValue || ''}
                  onChange={(e) => setEditModelValue(e.target.value as string)}
                  input={<OutlinedInput label="Model" />}
                  MenuProps={MenuProps}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {availableModels.map((model) => (
                    <MenuItem key={model.id} value={model.id}>
                      {model.label}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>
                  {!editValue ? 'Select a make first' : editValue && !editModelValue ? 'Please select a model' : ''}
                </FormHelperText>
              </FormControl>
            </Box>
          )}

          {/* Model Field */}
          {editField === 'model' && (
            <FormControl fullWidth disabled={availableModels.length === 0}>
              <InputLabel id="edit-model-label">Model</InputLabel>
              <Select
                labelId="edit-model-label"
                value={editValue || ''}
                onChange={(e) => setEditValue(e.target.value)}
                input={<OutlinedInput label="Model" />}
                MenuProps={MenuProps}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {availableModels.map((model) => (
                  <MenuItem key={model.id} value={model.id}>
                    {model.label}
                  </MenuItem>
                ))}
              </Select>
              {availableModels.length === 0 && (
                <FormHelperText>
                  {isIndividual ? 'No make assigned to this vehicle' : 'Select a make first for each vehicle'}
                </FormHelperText>
              )}
            </FormControl>
          )}

          {/* Colors Field */}
          {editField === 'colors' && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Select up to 3 colors{(editValue?.length || 0) > 0 && ` (${editValue.length} selected)`}
              </Typography>
              <ColorGrid
                colors={colors}
                selectedColors={editValue || []}
                onChange={setEditValue}
                maxColors={3}
                maxHeight={180}
              />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                Leave empty for no colors
              </Typography>
            </Box>
          )}

          {/* Body Field */}
          {editField === 'body' && (
            <FormControl fullWidth>
              <InputLabel id="edit-body-label">Body Style</InputLabel>
              <Select
                labelId="edit-body-label"
                value={editValue || ''}
                onChange={(e) => setEditValue(e.target.value)}
                input={<OutlinedInput label="Body Style" />}
                MenuProps={MenuProps}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {bodies.map((body) => (
                  <MenuItem key={body.id} value={body.id}>
                    {body.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {/* Type Field */}
          {editField === 'type' && (
            <FormControl fullWidth>
              <InputLabel id="edit-type-label">Type Style</InputLabel>
              <Select
                labelId="edit-type-label"
                value={editValue || ''}
                onChange={(e) => setEditValue(e.target.value)}
                input={<OutlinedInput label="Type Style" />}
                MenuProps={MenuProps}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {types.map((type) => (
                  <MenuItem key={type.id} value={type.id}>
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {/* Year Field */}
          {editField === 'year' && (
            <TextField
              fullWidth
              label="Year"
              value={editValue || ''}
              onChange={(e) => {
                const value = e.target.value;
                if (value === '' || /^\d+$/.test(value)) {
                  setEditValue(value);
                }
              }}
              placeholder="e.g., 2020"
              inputProps={{ inputMode: 'numeric' }}
            />
          )}

          {/* Flags Field */}
          {editField === 'flags' && (
            <FormControl fullWidth>
              <InputLabel id="edit-flags-label">Flags</InputLabel>
              <Select
                labelId="edit-flags-label"
                multiple
                value={editValue || []}
                onChange={(e) => setEditValue(e.target.value)}
                input={<OutlinedInput label="Flags" />}
                renderValue={(selected: string[]) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((flagId) => {
                      const flag = flags.find((f) => f.id === flagId);
                      return <Chip key={flagId} label={flag?.label || flagId} size="small" />;
                    })}
                  </Box>
                )}
                MenuProps={MenuProps}
              >
                {flags.map((flag) => (
                  <MenuItem key={flag.id} value={flag.id}>
                    {flag.label}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>Select one or more flags</FormHelperText>
            </FormControl>
          )}

          {/* Dataset Field */}
          {editField === 'dataset' && (
            <FormControl fullWidth>
              <InputLabel id="edit-dataset-label">Dataset</InputLabel>
              <Select
                labelId="edit-dataset-label"
                value={editValue || ''}
                onChange={(e) => setEditValue(e.target.value)}
                input={<OutlinedInput label="Dataset" />}
                MenuProps={MenuProps}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {datasets.map((dataset) => (
                  <MenuItem key={dataset.id} value={dataset.id}>
                    {dataset.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {/* Clear Body/Type/Year Field */}
          {editField === 'clearBodyTypeYear' && (
            <Typography color="text.secondary">
              This will clear body, type, and year from all {selectedCount} selected vehicles.
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={onSave}
          disabled={isSaveDisabled}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <EditIcon />}
        >
          {saving ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditDialog;
